import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Input,
  List,
  Spin,
  Empty,
  message,
  Checkbox,
  Space,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { searchProjectPages } from "../../services/projectPageService";
import { useNavigate } from "react-router-dom";
import CreateProjectPageModal from "./CreateProjectPageModal";

export default function CloneProjectPageModal({
  visible,
  onClose,
  onSuccess,
  projectId,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [cloneOptions, setCloneOptions] = useState({
    cloneComponents: true,
  });
  const debounceRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialForm, setInitialForm] = useState(null);

  // Search project pages with debounce
  useEffect(() => {
    const term = (search || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (term.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const pages = await searchProjectPages(term);
        setResults(Array.isArray(pages) ? pages : []);
      } catch (error) {
        message.error("Failed to search project pages");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // No need for filtering since search is done on backend
  const filteredResults = results;

  // Handle page selection
  const handleSelectPage = (page) => {
    setSelectedPage(page);
  };

  // Handle "clone" by opening the Create Page modal prefilled
  const handleClone = async () => {
    if (!selectedPage) {
      message.warning("Please select a project page to clone");
      return;
    }

    setCloning(true);
    try {
      const prefill = {
        projectPageName: selectedPage?.Name || "",
        projectPageType: selectedPage?.Project_Page_Type__c || "",
        active:
          selectedPage?.Active__c !== undefined
            ? Boolean(selectedPage.Active__c)
            : true,
        projectPageDescription: selectedPage?.Project_Page_Description__c || "",
      };

      setInitialForm(prefill);
      setShowCreateModal(true);

      // Reset and close search modal
      setSearch("");
      setSelectedPage(null);
      onClose && onClose();

      message.info("Creation form opened with page details. Review and Save.");
    } catch (error) {
      message.error(
        "Failed to prepare page: " +
          (error.response?.data?.error?.message || error.message),
      );
      console.error("Prefill error:", error);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setSelectedPage(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <CopyOutlined />
            <span>Clone Project Page</span>
          </Space>
        }
        open={visible}
        onCancel={handleCancel}
        onOk={handleClone}
        okText="Use in Creation"
        okButtonProps={{
          disabled: !selectedPage || cloning,
          loading: cloning,
        }}
        width={650}
        bodyStyle={{ padding: 16 }}
      >
        <div className="space-y-4">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for a project page to clone:
            </label>
            <Input.Search
              placeholder="Search by name (min 3 characters)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              size="large"
              loading={loading}
            />
          </div>

          {/* Page Results */}
          <div
            style={{
              maxHeight: "350px",
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {loading ? (
              <div className="text-center py-8">
                <Spin tip="Loading project pages..." />
              </div>
            ) : filteredResults.length > 0 ? (
              <List
                dataSource={filteredResults}
                renderItem={(item) => (
                  <List.Item
                    className={`cursor-pointer transition-colors px-4 ${
                      selectedPage?.Id === item.Id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleSelectPage(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <List.Item.Meta
                      title={
                        <div className="flex items-center">
                          {selectedPage?.Id === item.Id && (
                            <CopyOutlined className="mr-2 text-blue-500" />
                          )}
                          <span className="font-medium">{item.Name}</span>
                        </div>
                      }
                      description={
                        <div className="text-sm text-gray-500">
                          <div>Type: {item.Project_Page_Type__c || "N/A"}</div>
                          <div>Active: {item.Active__c ? "Yes" : "No"}</div>
                          {item.Project__r?.Name && (
                            <div>Project: {item.Project__r.Name}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : search.length >= 3 ? (
              <Empty description="No project pages found" className="py-8" />
            ) : (
              <Empty
                description="Type at least 3 characters to search"
                className="py-8"
              />
            )}
          </div>

          {/* Clone Options */}
          {selectedPage && (
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Clone Options:
              </label>
              <Checkbox
                checked={cloneOptions.cloneComponents}
                onChange={(e) =>
                  setCloneOptions({ cloneComponents: e.target.checked })
                }
              >
                <span className="font-medium">Include Page Components</span>
                <div className="text-xs text-gray-500 ml-6">
                  Clone all components associated with this page
                </div>
              </Checkbox>
            </div>
          )}

          {/* Selected Page Info */}
          {selectedPage && (
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
              <div className="text-sm">
                <div className="font-medium text-blue-900">
                  Selected: {selectedPage.Name}
                </div>
                <div className="text-blue-700 text-xs mt-1">
                  A new page will be created with all properties copied
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <CreateProjectPageModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          if (onSuccess) onSuccess();
        }}
        initialValues={initialForm || {}}
      />
    </>
  );
}
